import type { Country as CountryLookupOption } from "@globalgiving/vast-frontend-common"
import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { config } from "~/config"
import { axiosGet } from "~/lib"
import { Country, ErrorResponse } from "~/types"
import { ONE_DAY_MILLISECONDS } from "~/utils"

import { queryKeys } from "../query-keys"

const getCountries = async (params?: URLSearchParams): Promise<Country[]> => {
  const { data } = await axiosGet<{ data: Country[] }>({
    url: `${config.countryApiBaseUrl}/countries`,
    params,
  })

  const uniqueCountriesObj = data.reduce(
    (acc, country) => {
      acc[country.code] = country
      return acc
    },
    {} as Record<string, Country>
  )

  return Object.values(uniqueCountriesObj)
}

export const countryQueryOptions: UseQueryOptions<Country[], ErrorResponse, Country[], string[]> = {
  queryKey: queryKeys.countries(),
  queryFn: () => getCountries(),
  staleTime: ONE_DAY_MILLISECONDS,
}

export const useCountries = () => {
  return useQuery(countryQueryOptions)
}

const selectCountryOptions = (countries: Country[]): CountryLookupOption[] =>
  countries
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((country: Country) => ({
      value: country.code,
      label: country.name,
      restricted: country.restricted,
      currency: country.currency,
      isFXecuteEligible: country.is_fxecute_eligible,
    }))

export const getCountryOptionsQueryOptions = (
  params?: URLSearchParams
): UseQueryOptions<Country[], ErrorResponse, CountryLookupOption[], string[]> => ({
  queryKey: queryKeys.countries(params),
  queryFn: () => getCountries(params),
  staleTime: ONE_DAY_MILLISECONDS,
  select: selectCountryOptions,
})
